// @ts-nocheck
/*
 * Agents list — v1 logic preserved end-to-end. 10s polling, dual render
 * modes (regular vs consolidatedCallCentre for call-centre-admin overview),
 * CLI allocation with dedicated-CLI radio, bulk CSV upload + result modal,
 * bulk logout, per-row login-as flow (Soft Phone / Mobile / WEBRTC),
 * country codes / activity / call-quality modals, agent-details modal.
 *
 * Chrome swap: react-bootstrap Row/Col/Card/Modal/Form/Button/Dropdown →
 * shadcn Card/Dialog/ConfirmDialog/DropdownMenu + native form-control
 * inputs and search bar. react-select and existing partial modals stay.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { canBulkUploadAgents } from '@/utilities/promptUploadAccess';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Loader2, Search } from 'lucide-react';
import {
  Flag,
  Phone,
  Mail,
  User,
  Calendar,
  CalendarClock,
  Shield,
  Info,
  Eye,
  PhoneForwarded,
  Activity as ActivityIcon,
  Signal,
  MoreVertical,
  Ban,
  Smartphone,
  Radio,
  Headphones as HeadphonesIcon,
  LogIn,
} from 'lucide-react';
import { FiInfo, FiEdit } from 'react-icons/fi';
import type { ColumnDef } from '@tanstack/react-table';
import ReactSelect from 'react-select';
import { DataTable } from '@/components/data-table/DataTable';
import downloadTemplate from '@/utilities/downloadCsv';
import Loader from '@/components/partials/components/Loader';
import {
  API_AGENTS_LIST,
  API_AGENT_BULK_LOGOUT,
  API_AGENT_BULK_UPLOAD,
  API_AGENT_AVAILABLE_INBOUND_NUMBERS_BY_ID,
  API_AGENT_CLI_ALLOCATIONS_SYNC,
  API_USER_COUNTRY_CODE_DIRECTIONS_BY_AGENT_IDS,
  API_SWITCH_USER,
  API_USERS_LIST,
  API_USER_STATUS_UPDATE,
} from '@/service/url';
import CountryCodeDirectionsModal from '@/components/partials/dashboard/country-code-direction/CountryCodeDirectionsModal';
import AgentActivityModal from '@/features/agent-activity/AgentActivityModal';
import SipTraceModal from '@/features/sip-trace/SipTraceModal';
import AgentCallQualityModal from './AgentCallQualityModal';
import BreakStatusCapsule from '@/components/BreakStatusCapsule';
import { getApiCall, postApiCall, postApiMediaCall, patchApiCallWithHeader } from '@/service/httpCall';
import {
  UPLOAD_BULK_AGENT_CREATE,
  UPLOAD_BULK_AGENT_CREATE_REQUIREMENTS,
  UPLOAD_BULK_AGENT_CREATE_SAMPLE_DATE,
} from '@/utilities/constant';
import InfoNote from '@/components/InfoNote';
import { getUser, getUserReadAccessRoleLabel, switchUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES, ROUTES } from '@/utilities/constant';
import RenderInactiveUserModal from '@/components/partials/components/inactive-user-modal';
import RenderShowPasswordModal from '@/components/partials/components/show-password';
import RenderUserHierarchyModal from '@/components/partials/components/user-hierarchy-modal';
import { exportToCSV, hasPermission } from '@/utilities/helpers';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Download, Plus, Network } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const normalizeBulkAgentResult = (response) => {
  const responseData = response?.data?.data || response?.data || response;
  const successRows = Array.isArray(responseData?.success) ? responseData.success : [];
  const failedRows = Array.isArray(responseData?.failed) ? responseData.failed : [];
  return {
    totalProcessed: successRows.length + failedRows.length,
    successCount: successRows.length,
    failedCount: failedRows.length,
    successRows,
    failedRows,
  };
};

const getCsvField = (row, name) => {
  if (!row || typeof row !== 'object') return undefined;
  if (row[name] !== undefined) return row[name];
  if (row[`${name} - Required`] !== undefined) return row[`${name} - Required`];
  if (row[`${name} - Optional`] !== undefined) return row[`${name} - Optional`];
  return undefined;
};

const formatSuccessAgent = (item, index) => {
  const a = item?.agent || {};
  const name = a.agent_name || getCsvField(item?.row, 'Agent Name') || '-';
  const number = a.agent_number || getCsvField(item?.row, 'Agent Number') || '-';
  const username = a.username || getCsvField(item?.row, 'Username') || '-';
  return `Row ${index + 1} | ${name} | Number: ${number} | Username: ${username}`;
};

const formatFailedAgent = (item, index) => {
  const row = item?.row || {};
  const name = getCsvField(row, 'Agent Name') || '-';
  const number = getCsvField(row, 'Agent Number') || '-';
  const username = getCsvField(row, 'Username') || '-';
  const error = item?.error || 'Unknown error';
  return `Row ${index + 1} | ${name} | Number: ${number} | Username: ${username} | Reason: ${error}`;
};

const formatAgentAclDisplay = (details) => {
  if (!details) return 'Not Assigned';
  const acl = details.linkedUser?.acl_settings;
  const aclId = acl?.id ?? details.linkedUser?.acl_settings_id;
  if (acl?.acl_name && aclId) return `${acl.acl_name} (ID: ${aclId})`;
  if (acl?.acl_name) return acl.acl_name;
  if (aclId) return `ID: ${aclId}`;
  return 'Not Assigned';
};

const isAllocateCliSelectNode = (target: EventTarget | null | undefined) => {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest('[class*="allocate-cli-select"]'));
};

const EXTENSION_PROFILE_TYPES = ['WEBRTC', 'SOFT PHONE'];

const getAgentExtensions = (rowData: any) => {
  const profiles = rowData?.agentTelephony?.profile;
  if (!Array.isArray(profiles)) return '-';
  const extensions = profiles
    .filter((p: any) =>
      EXTENSION_PROFILE_TYPES.includes(String(p?.type || '').toUpperCase()),
    )
    .map((p: any) => p?.number?.number)
    .filter(Boolean)
    .map(String);
  return extensions.length ? [...new Set(extensions)].join(', ') : '-';
};

const AgentsListCenterCell = ({ children }: any) => (
  <div className="agents-list-cell-center">{children}</div>
);

const AgentListInlineButton = ({ children, onClick, disabled, title }: any) => (
  <Button
    type="button"
    size="sm"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="gap-1.5 px-2.5 py-1 text-xs"
  >
    {children}
  </Button>
);

const Agents = ({ consolidatedCallCentre = false }: any) => {
  const navigate = useNavigate();
  const [agentList, setAgentList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState<any[]>([]);
  const [statusList, setStatusList] = useState<any>({});
  const [buttonDisable, setButtonDisable] = useState(false);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [bulkFile, setBulkFile] = useState<any>(null);
  const [showBulkResultModal, setShowBulkResultModal] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [newStatus, setNewStatus] = useState(false);
  const [agentId, setAgentId] = useState<any>('');
  const [activeType, setActiveType] = useState('');
  const [types, setTypes] = useState<any[]>([]);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [loggedInUserId, setLoggedInUserId] = useState<any>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loggingInType, setLoggingInType] = useState<any>(null);
  const [pendingTypeLogin, setPendingTypeLogin] = useState<{
    agentId: any;
    agentName: string;
    type: string;
    isActive: boolean;
  } | null>(null);
  const [showBulkLogoutModal, setShowBulkLogoutModal] = useState(false);
  const [bulkLogoutLoading, setBulkLogoutLoading] = useState(false);
  const bulkLogoutInFlightRef = useRef(false);
  const [showCcDirectionsModal, setShowCcDirectionsModal] = useState(false);
  const [ccDirectionsData, setCcDirectionsData] = useState<any[]>([]);
  const [ccDirectionsOwner, setCcDirectionsOwner] = useState('');
  const [, setIsFirstLoad] = useState(true);
  const [showAllocateCliModal, setShowAllocateCliModal] = useState(false);
  const [allocatingAgent, setAllocatingAgent] = useState<any>(null);
  const [availableCliDids, setAvailableCliDids] = useState<any[]>([]);
  const [selectedCliDidIds, setSelectedCliDidIds] = useState<number[]>([]);
  const [dedicatedCliNumberId, setDedicatedCliNumberId] = useState<any>(null);
  const [loadingCliDids, setLoadingCliDids] = useState(false);
  const [savingCliDid, setSavingCliDid] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [activityAgent, setActivityAgent] = useState<any>(null);
  const [showHierarchyModal, setShowHierarchyModal] = useState(false);
  const [hierarchyUser, setHierarchyUser] = useState<any>(null);
  const [sipTraceAgent, setSipTraceAgent] = useState<any>(null);
  const [callQualityAgent, setCallQualityAgent] = useState<any>(null);
  const [showViewClisModal, setShowViewClisModal] = useState(false);
  const [viewClisAgent, setViewClisAgent] = useState<any>(null);
  const [viewClisList, setViewClisList] = useState<any[]>([]);
  const [viewClisLoading, setViewClisLoading] = useState(false);
  const [selectedAgentDetails, setSelectedAgentDetails] = useState<any>(null);
  const [showAgentDetailsModal, setShowAgentDetailsModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => Math.floor(Date.now() / 1000));
  const [enableInactiveModal, setEnableInactiveModal] = useState(false);
  const [inactiveUserId, setInactiveUserId] = useState<any>(undefined);
  const [confirmUserInactive, setConfirmUserInactive] = useState(false);
  const [enableShowPasswordModal, setEnableShowPasswordModal] = useState(false);
  const [showPasswordId, setShowPasswordId] = useState<any>(undefined);
  const [, setConfirmShowPassword] = useState(false);
  const [showPasswordUser, setShowPasswordUser] = useState<any>(null);
  const [activeUserAccessRole, setActiveUserAccessRole] = useState<any>(undefined);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState<any>({});
  const [, setShowToast] = useState(false);
  const [, setToastProps] = useState<any>(null);

  const agentsListPath = `/${ROUTES.MANAGE_AGENTS.AGENTS}`;
  const setAgentsReturnPath = () => {
    if (consolidatedCallCentre) sessionStorage.setItem('agentsReturnTo', agentsListPath);
    else sessionStorage.removeItem('agentsReturnTo');
  };

  const getColumnsAndFilename = () => {
    const columns = UPLOAD_BULK_AGENT_CREATE;
    const requirements = UPLOAD_BULK_AGENT_CREATE_REQUIREMENTS;
    const sample_data = UPLOAD_BULK_AGENT_CREATE_SAMPLE_DATE;
    const filename = 'Bulk_Create_Agents.csv';
    return { columns, requirements, filename, sample_data };
  };

  const fetchAgents = (showLoader = false) => {
    if (showLoader) setLoading(true);
    getApiCall(API_AGENTS_LIST)
      .then((resp: any) => {
        if (resp) {
          setAgentList((prev) => {
            const prevDirectionsById = new Map(
              prev.map((agent) => [agent.id, agent.country_code_directions]),
            );
            return resp?.map((val) => ({
              id: val.id,
              agent_name: val.agent_name,
              agent_number: val.agent_number,
              is_allocated: val.is_allocated,
              created_by: val.created_by?.username || 'Unknown',
              created_by_id: val.created_by?.id ?? null,
              created_at: new Date(val.created_at).toLocaleString(),
              created_at_raw: val.created_at,
              extension: val.extension?.map((ext) => ext.extension).join(', ') || 'N/A',
              username: val.username,
              type: val.type,
              access: val.access,
              email_id: val.email_id,
              status: val.login_status === '1' ? 'Active' : 'Inactive',
              is_logged_in: val.linkedUser?.login_at != null && val.linkedUser?.login_at !== undefined,
              account_status: val.linkedUser?.status,
              expiry_date: val.linkedUser?.expiry_date || null,
              cli_option: val.cli_option ? val.cli_option.toUpperCase() : '-',
              is_outbound_enabled: val.is_outbound_enabled === true,
              is_masking_enabled: val.is_masking_enabled === true,
              agentTelephony: val.agentTelephony,
              country_code_directions: prevDirectionsById.get(val.id) || [],
              first_call_acceptance: val.first_call_acceptance?.toUpperCase(),
              break_id: val.break_id,
              breakDetail: val.breakDetail,
              agent_updated_at: val.updated_at,
              linkedUser: val.linkedUser || null,
              activeUserBreak: val.activeUserBreak || null,
            }));
          });

          const ids = resp.map((val) => val.id);
          if (ids.length > 0) {
            getApiCall(`${API_USER_COUNTRY_CODE_DIRECTIONS_BY_AGENT_IDS}?ids=${ids.join(',')}`)
              .then((map: any) => {
                if (!map || typeof map !== 'object') return;
                setAgentList((prev) =>
                  prev.map((a) => ({
                    ...a,
                    country_code_directions:
                      map[a.id] !== undefined ? map[a.id] || [] : a.country_code_directions,
                  })),
                );
              })
              .catch(() => {});
          }

          setStatusList(
            resp.reduce((acc, val) => {
              acc[val.id] = val.login_status === '1';
              return acc;
            }, {}),
          );
        } else {
          toast.error('Failed to fetch agents');
        }
      })
      .catch((err) => {
        console.error('Fetch Error:', err);
        toast.error('Error fetching agents');
      })
      .finally(() => {
        if (showLoader) setLoading(false);
      });
  };

  useEffect(() => {
    const user = getUser();
    if (consolidatedCallCentre && user?.role !== USER_ROLES.CALL_CENTRE_ADMIN) {
      navigate(`/${ROUTES.MANAGE_AGENTS.AGENTS}`, { replace: true });
      return;
    }
    setLoggedInUserId(user?.id ?? null);
    if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    if (consolidatedCallCentre) {
      const roleLabels = getUserReadAccessRoleLabel();
      const role = roleLabels?.[0]?.value;
      if (role) setActiveUserAccessRole(role);
    }
    fetchAgents(true);
    setIsFirstLoad(false);
    const interval = setInterval(() => fetchAgents(false), 10000);
    return () => clearInterval(interval);
  }, [consolidatedCallCentre]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!confirmUserInactive || !inactiveUserId) return;
    patchApiCallWithHeader(`${API_USER_STATUS_UPDATE}/${inactiveUserId}`, { status: 0 })
      .then(() => {
        toast.success('Agent blocked successfully');
        setEnableInactiveModal(false);
        setInactiveUserId(undefined);
        setConfirmUserInactive(false);
        fetchAgents();
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Unable to block agent');
        setConfirmUserInactive(false);
      });
  }, [confirmUserInactive, inactiveUserId]);

  useEffect(() => {
    if (!consolidatedCallCentre || !enableShowPasswordModal || !showPasswordId || !activeUserAccessRole)
      return;
    getApiCall(`${API_USERS_LIST}/${showPasswordId}`, { role: activeUserAccessRole })
      .then((resp: any) => {
        if (resp?.actual_password) setShowPasswordUser(`Password is : ${resp.actual_password}`);
        else setShowPasswordUser('Actual Password is not saved for this agent');
        setShowPasswordId(undefined);
        setConfirmShowPassword(false);
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Unable to fetch password');
        setEnableShowPasswordModal(false);
      });
  }, [enableShowPasswordModal, consolidatedCallCentre, showPasswordId, activeUserAccessRole]);

  const openAgentDetailsModal = (rowData) => {
    setSelectedAgentDetails(rowData);
    setShowAgentDetailsModal(true);
  };

  const handleBulkAdd = () => setShowBulkAddModal(true);
  const handleBulkAddFileChange = (event) => setBulkFile(event.target.files[0]);

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings)
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleBulkAddSave = async () => {
    if (!bulkFile) return void toast.error('Please select a file to upload.');
    const fileName = bulkFile.name.toLowerCase();
    if (!fileName.endsWith('.csv')) return void toast.error('Invalid file type. Please upload a CSV file.');
    const formData = new FormData();
    formData.append('file', bulkFile);
    try {
      const response = await postApiMediaCall(API_AGENT_BULK_UPLOAD, formData);
      const normalized = normalizeBulkAgentResult(response);
      setBulkUploadResult(normalized);
      setShowBulkAddModal(false);
      setBulkFile(null);
      setShowBulkResultModal(true);

      if (normalized.successCount > 0 && normalized.failedCount === 0) {
        toast.success(`${normalized.successCount} agent(s) uploaded successfully.`);
      } else if (normalized.successCount > 0) {
        toast.warning(`Uploaded ${normalized.successCount} agent(s); ${normalized.failedCount} row(s) failed.`);
      } else {
        toast.error(`No agents were created. ${normalized.failedCount} row(s) failed validation.`);
      }
      fetchAgents();
    } catch (err: any) {
      const errorMessage = friendlyApiError(err) || 'Failed to upload bulk agents';
      toast.error(
        `${errorMessage}\n\nPlease ensure:\n- File is in CSV format\n- All required fields are present\n- Data format is correct\n- Download and use the template for reference`,
      );
      setShowBulkAddModal(false);
      setBulkFile(null);
    }
  };

  const handleStatusToggle = async (agentIdArg, agentTelephony, typeArg) => {
    const nextStatus = !statusList[agentIdArg];
    setNewStatus(nextStatus);
    setAgentId(agentIdArg);
    const nextTypes = agentTelephony?.map((item) => item.type);
    setTypes(nextTypes);
    let nextActiveType = typeArg;
    if (!nextActiveType && agentTelephony) {
      const activeProfile = agentTelephony.find((p) => p.active_profile === true);
      nextActiveType = activeProfile?.type;
    }
    try {
      if (nextStatus) {
        setShowModal(true);
      } else {
        if (!nextActiveType) {
          toast.error(`Cannot logout: No active login type found for this agent`);
          return;
        }
        const params = { type: nextActiveType, login_status: nextStatus ? '1' : '0' };
        toast.success(`Logged Out From: ${nextActiveType}`);
        setActiveType('');
        fetchAgents();
      }
    } catch {
      toast.error(`Error while Logged Out From: ${nextActiveType || 'Unknown'}`);
    }
  };

  async function loginAs(typeArg) {
    try {
      setIsLoggingIn(true);
      setLoggingInType(typeArg);
      const params = { type: typeArg, login_status: newStatus ? '1' : '0' };
      toast.success(`Logged In as: ${typeArg}`);
      setActiveType('');
      setShowModal(false);
      fetchAgents();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || `Error while Logged In as: ${typeArg}`);
    } finally {
      setIsLoggingIn(false);
      setLoggingInType(null);
    }
  }

  const confirmTypeLogin = async () => {
    if (!pendingTypeLogin) return;
    const { agentId: aid, type: typeArg } = pendingTypeLogin;
    try {
      setIsLoggingIn(true);
      setLoggingInType(typeArg);
      const params = { type: typeArg, login_status: '1' };
      // toggleStatus API disabled — unguarded licence writer, races with logoutUser/logoutAs/reaper causing licence count drift.
      // await postApiCall(`/v2/agents/status/${aid}`, params);
      toast.success(`Logged In as: ${typeArg}`);
      fetchAgents();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || `Error while Logged In as: ${typeArg}`);
    } finally {
      setIsLoggingIn(false);
      setLoggingInType(null);
      setPendingTypeLogin(null);
    }
  };

  const loggedInCompanyDemo = (() => {
    try {
      const u = getUser();
      const c = u?.assignedCompany || u?.assigned_company_details || u?.company || null;
      return c?.is_demo_company === true;
    } catch {
      return false;
    }
  })();

  const handleAgentLogout = () => {
    if (selectedAgents.length === 0 || bulkLogoutInFlightRef.current) return;
    bulkLogoutInFlightRef.current = true;
    setBulkLogoutLoading(true);
    postApiCall(API_AGENT_BULK_LOGOUT, { agentIds: selectedAgents })
      .then(() => {
        toast.success('Agents Logged Out successfully');
        fetchAgents();
        setSelectedAgents([]);
        setShowBulkLogoutModal(false);
      })
      .catch((err) => {
        toast.error(friendlyApiError(err));
        fetchAgents();
        setSelectedAgents([]);
      })
      .finally(() => {
        bulkLogoutInFlightRef.current = false;
        setBulkLogoutLoading(false);
      });
  };

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'agents-list',
        data: agentList,
        columns: [
          { header: 'Agent Name', accessor: 'agent_name' },
          { header: 'Agent Number', accessor: 'agent_number' },
          { header: 'Access', accessor: (row) => row.access || '-' },
          { header: 'Email ID', accessor: (row) => row.email_id || '-' },
          { header: 'Extensions', accessor: (row) => getAgentExtensions(row) },
          { header: 'Cli Option (Webrtc)', accessor: (row) => row.cli_option || '-' },
          { header: 'Outbound Enabled', accessor: (row) => (row.is_outbound_enabled ? 'Yes' : 'No') },
          { header: 'Number Masking', accessor: (row) => (row.is_masking_enabled ? 'Yes' : 'No') },
          { header: 'Created By', accessor: (row) => row.created_by || '-' },
          { header: 'Joining Date', accessor: (row) => row.created_at || '-' },
          {
            header: 'Allocation Status',
            accessor: (row) => (Number(row.is_allocated) === 0 ? 'Available' : 'Allocated'),
          },
          { header: 'Status', accessor: (row) => row.status || '-' },
          {
            header: 'Login Types',
            accessor: (row) => row.agentTelephony?.profile?.map((item) => item.type)?.join(', ') || '-',
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const onCheckboxChange = (checked, agentIdArg) =>
    setSelectedAgents((prev) => (checked ? [...prev, agentIdArg] : prev.filter((id) => id !== agentIdArg)));
  const onHeaderCheckboxChange = (checked) => {
    if (checked) setSelectedAgents(agentList?.map((a) => a.id));
    else setSelectedAgents([]);
  };

  const allocationStatusBodyTemplate = (rowData) =>
    Number(rowData.is_allocated) === 0 ? 'Available' : 'Allocated';

  const handleEditAgent = (agentIdArg) => {
    setAgentsReturnPath();
    const editPath = ROUTES.MANAGE_AGENTS.EDIT_AGENT.replace(':id', agentIdArg);
    navigate(`/${editPath}`);
  };

  const handleBlockAgent = (linkedUserId) => {
    setInactiveUserId(linkedUserId);
    setEnableInactiveModal(true);
  };

  const handleUnblockAgent = (linkedUserId) => {
    patchApiCallWithHeader(`${API_USER_STATUS_UPDATE}/${linkedUserId}`, { status: 1 })
      .then(() => {
        toast.success('Agent unblocked successfully');
        fetchAgents();
      })
      .catch((err) => toast.error(friendlyApiError(err) || 'Unable to unblock agent'));
  };

  const handleShowAgentPassword = (linkedUserId) => {
    setShowPasswordId(linkedUserId);
    setEnableShowPasswordModal(true);
  };

  const handleResetAgentPassword = (linkedUserId) => {
    setAgentsReturnPath();
    const resetRoute = ROUTES.USERS.RESET_PASSWORD.replace(':id', linkedUserId);
    navigate(`/${resetRoute}`);
  };

  async function switchToAgentAccount(linkedUserId, accountStatus) {
    if (isSwitchingAccount) return;
    if (!linkedUserId) {
      toast.error('Agent user account not found');
      return;
    }
    if (Number(accountStatus) === 0 || accountStatus === 'Blocked') {
      toast.error('Agent is blocked');
      return;
    }
    setIsSwitchingAccount(true);
    try {
      const response: any = await postApiCall(API_SWITCH_USER, { id: linkedUserId });
      const switchAccountSuccess = await switchUser(response);
      if (switchAccountSuccess) {
        toast.success('Account switched successfully');
        setTimeout(() => {
          window.location.href = `/${ROUTES.DASHBOARD}`;
        }, 1500);
      } else {
        setIsSwitchingAccount(false);
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to switch account');
      setIsSwitchingAccount(false);
    }
  }

  const formatDateTime = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isDirectParentOfAgent = (rowData) => {
    if (isSuperAdmin) return true;
    if (loggedInUserId == null || rowData?.created_by_id == null) return false;
    return Number(rowData.created_by_id) === Number(loggedInUserId);
  };

  const hierarchyTargetForAgent = (rowData) => {
    if (rowData?.linkedUser?.id)
      return { id: rowData.linkedUser.id, username: rowData.username || rowData.agent_name };
    if (rowData?.created_by_id != null)
      return { id: rowData.created_by_id, username: rowData.created_by };
    return null;
  };

  const openHierarchyModal = (rowData) => {
    const target = hierarchyTargetForAgent(rowData);
    if (!target) return;
    setHierarchyUser(target);
    setShowHierarchyModal(true);
  };

  const renderSwitchToAgentCell = (rowData) => {
    const linkedUserId = rowData.linkedUser?.id;
    const accountStatus = rowData.account_status;
    const canSwitch = isDirectParentOfAgent(rowData);
    const active =
      Number(accountStatus) === 1 || accountStatus === 'Active' || accountStatus === 1;

    if (!linkedUserId || !canSwitch) return null;

    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        title={
          !active
            ? 'Cannot switch to a blocked agent'
            : isSwitchingAccount
              ? 'Switching account…'
              : `Switch to ${rowData.username || rowData.agent_name}`
        }
        disabled={!active || isSwitchingAccount}
        onClick={() => switchToAgentAccount(linkedUserId, accountStatus)}
      >
        <LogIn className="h-4 w-4" />
      </Button>
    );
  };

  const renderConsolidatedActions = (rowData) => {
    const linkedUserId = rowData.linkedUser?.id;
    const canEditUsers = loggedInUserACL?.[MODULE_ACL.USERS]?.edit;
    const isOwnAgent = isDirectParentOfAgent(rowData);
    const canEditAgents = isOwnAgent && (isSuperAdmin || loggedInUserACL?.[MODULE_ACL.AGENTS]?.edit);
    const accountStatus = rowData.account_status;
    const isBlocked = accountStatus === 0 || accountStatus === 'Blocked';

    return (
      <div className="agents-list-actions-cell">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
              title="Row actions"
              aria-label="Row actions"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="max-h-[320px] overflow-y-auto">
            {linkedUserId && isOwnAgent && (
              <DropdownMenuItem
                disabled={isBlocked || isSwitchingAccount}
                onClick={() => switchToAgentAccount(linkedUserId, accountStatus)}
              >
                Switch To Agent
              </DropdownMenuItem>
            )}
            {canEditAgents && (
              <DropdownMenuItem onClick={() => handleEditAgent(rowData.id)}>Edit Agent</DropdownMenuItem>
            )}
            {canEditAgents && (
              <DropdownMenuItem
                disabled={rowData.is_logged_in}
                onClick={() => openAllocateCliModal(rowData)}
              >
                Allocate CLI
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setActivityAgent(rowData)}>View Activity</DropdownMenuItem>
            {isSuperAdmin && (
              <DropdownMenuItem onClick={() => setSipTraceAgent(rowData)}>WebRTC Trace</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setCallQualityAgent(rowData)}>
              Call Quality Stats
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openViewClisModal(rowData)}>View CLIs</DropdownMenuItem>
            {hierarchyTargetForAgent(rowData) && (
              <DropdownMenuItem onClick={() => openHierarchyModal(rowData)}>
                View Hierarchy
              </DropdownMenuItem>
            )}
            {linkedUserId && canEditUsers && !loggedInCompanyDemo ? (
              isBlocked ? (
                <DropdownMenuItem onClick={() => handleUnblockAgent(linkedUserId)}>
                  Unblock Agent
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => handleBlockAgent(linkedUserId)}>
                  Block Agent
                </DropdownMenuItem>
              )
            ) : null}
            {linkedUserId && (
              <DropdownMenuItem onClick={() => handleShowAgentPassword(linkedUserId)}>
                Show Password
              </DropdownMenuItem>
            )}
            {linkedUserId && canEditUsers && (
              <DropdownMenuItem onClick={() => handleResetAgentPassword(linkedUserId)}>
                Reset Password
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const openAllocateCliModal = async (rowData) => {
    if (rowData.is_logged_in) {
      toast.error(
        'Cannot allocate CLI: Agent is currently logged in. Please ask the agent to log out first.',
      );
      return;
    }
    setAllocatingAgent(rowData);
    setSelectedCliDidIds([]);
    setDedicatedCliNumberId(null);
    setShowAllocateCliModal(true);
    setLoadingCliDids(true);
    try {
      const list: any = await getApiCall(
        API_AGENT_AVAILABLE_INBOUND_NUMBERS_BY_ID.replace(':id', rowData.id),
      );
      setAvailableCliDids(Array.isArray(list) ? list : []);
      const current = (Array.isArray(list) ? list : [])
        .filter((n) => n.cli_target_type === 'AGENT' && String(n.cli_target_id) === String(rowData.id))
        .map((n) => Number(n.id))
        .filter((id) => Number.isFinite(id));
      setSelectedCliDidIds(current);
      const dedicatedMatch = (Array.isArray(list) ? list : []).find(
        (n) =>
          current.includes(Number(n.id)) &&
          n.inbound_routing_type === 'AGENT' &&
          String(n.inbound_routing_id) === String(rowData.id),
      );
      setDedicatedCliNumberId(
        dedicatedMatch && Number.isFinite(Number(dedicatedMatch.id)) ? Number(dedicatedMatch.id) : null,
      );
    } catch {
      toast.error('Failed to fetch available CLIs for allocation');
      setAvailableCliDids([]);
    } finally {
      setLoadingCliDids(false);
    }
  };

  const handleCliDidSelect = (values) => {
    const ids = (values || []).map((v) => Number(v)).filter((v) => Number.isFinite(v));
    setSelectedCliDidIds((prev) => {
      const kept = prev.filter((id) => ids.includes(id));
      const appended = ids.filter((id) => !kept.includes(id));
      return [...kept, ...appended];
    });
    setDedicatedCliNumberId((prev) => (prev != null && ids.includes(prev) ? prev : null));
  };

  const handleSaveAllocatedCliDid = async () => {
    if (!allocatingAgent?.id || savingCliDid) return;
    setSavingCliDid(true);
    try {
      await postApiCall(API_AGENT_CLI_ALLOCATIONS_SYNC.replace(':id', allocatingAgent.id), {
        numberIds: selectedCliDidIds,
        defaultNumberId: selectedCliDidIds?.[0] || null,
        dedicatedNumberIds: dedicatedCliNumberId != null ? [dedicatedCliNumberId] : [],
      });
      toast.success('CLI allocated successfully');
      setShowAllocateCliModal(false);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to allocate CLI');
    } finally {
      setSavingCliDid(false);
    }
  };

  const openViewClisModal = async (agent) => {
    if (!agent?.id) return;
    setViewClisAgent(agent);
    setViewClisList([]);
    setShowViewClisModal(true);
    setViewClisLoading(true);
    try {
      const list: any = await getApiCall(
        API_AGENT_AVAILABLE_INBOUND_NUMBERS_BY_ID.replace(':id', agent.id),
      );
      const allocated = (Array.isArray(list) ? list : []).filter(
        (n) => n.cli_target_type === 'AGENT' && String(n.cli_target_id) === String(agent.id),
      );
      setViewClisList(allocated);
    } catch {
      toast.error('Failed to fetch allocated CLIs');
      setViewClisList([]);
    } finally {
      setViewClisLoading(false);
    }
  };

  const isCallCentreAdmin = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.AGENTS,
    'read',
  );
  const canAdd =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.AGENTS, 'add');
  const canEdit =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.AGENTS, 'edit');
  const canBlock = canEdit;
  const showActionsColumn = isCallCentreAdmin || isSuperAdmin;

  const filteredAgents = useMemo(() => {
    if (!globalFilter) return agentList;
    const q = globalFilter.toLowerCase();
    return agentList.filter((a) => (
      String(a.agent_name || '').toLowerCase().includes(q) ||
      String(a.agent_number || '').toLowerCase().includes(q) ||
      String(a.username || '').toLowerCase().includes(q) ||
      String(a.access || '').toLowerCase().includes(q) ||
      String(a.email_id || '').toLowerCase().includes(q) ||
      String(a.cli_option || '').toLowerCase().includes(q) ||
      String(a.first_call_acceptance || '').toLowerCase().includes(q) ||
      String(a.created_by || '').toLowerCase().includes(q) ||
      String(a.created_at || '').toLowerCase().includes(q)
    ));
  }, [agentList, globalFilter]);

  if (!canRead) {
    return (
      <div className="my-12 p-4 text-center text-base">
        No read permission found in assigned ACL
      </div>
    );
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      header: () => (
        <Checkbox
          checked={selectedAgents.length === agentList.length && agentList.length > 0}
          onCheckedChange={onHeaderCheckboxChange}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedAgents.includes(row.original.id)}
          onCheckedChange={(v) => onCheckboxChange(v, row.original.id)}
        />
      ),
    },
    { accessorKey: 'agent_name', header: 'Agent Name' },
    { accessorKey: 'agent_number', header: 'Agent Number' },
    {
      accessorKey: 'username',
      header: 'Username',
      cell: ({ row }) => row.original.username || row.original.linkedUser?.username || '-',
    },
    ...(consolidatedCallCentre
      ? [
          {
            id: 'details',
            header: 'Details',
            cell: ({ row }) => (
              <AgentsListCenterCell>
                <button
                  type="button"
                  onClick={() => openAgentDetailsModal(row.original)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                  title="View details"
                  aria-label="View details"
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
              </AgentsListCenterCell>
            ),
          } as ColumnDef<any>,
        ]
      : ([
          { accessorKey: 'email_id', header: 'Email' },
          { id: 'expiry', header: 'Expiry Date', cell: ({ row }) => formatDateTime(row.original.expiry_date) },
          {
            id: 'agent_details',
            header: 'Details',
            cell: ({ row }) => (
              <button
                type="button"
                onClick={() => openAgentDetailsModal(row.original)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                title="View details"
                aria-label="View details"
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
            ),
          },
        ] as ColumnDef<any>[])),
    {
      id: 'break_status',
      header: 'Break Status',
      cell: ({ row }) => (
        <AgentsListCenterCell>
          <BreakStatusCapsule
            agent={{
              break_id: row.original.break_id,
              breakDetail: row.original.breakDetail,
              updated_at: row.original.agent_updated_at,
            }}
            user={row.original.linkedUser}
            activeUserBreak={row.original.activeUserBreak}
            currentTime={currentTime}
          />
        </AgentsListCenterCell>
      ),
    },
    {
      accessorKey: 'access',
      header: 'Access',
      cell: ({ row }) => {
        const val = row.original.access;
        return val === 'team_lead'
          ? 'TEAM LEAD'
          : val === 'regular'
            ? 'REGULAR'
            : val
              ? String(val).toUpperCase()
              : '-';
      },
    },
    ...(!consolidatedCallCentre
      ? ([
          { accessorKey: 'first_call_acceptance', header: 'First Call Acceptance' },
          { accessorKey: 'cli_option', header: 'Cli Option (Webrtc)' },
          {
            id: 'outbound_enabled',
            header: 'Outbound Enabled',
            cell: ({ row }) => (row.original.is_outbound_enabled ? 'Yes' : 'No'),
          },
          {
            id: 'number_masking',
            header: 'Number Masking',
            cell: ({ row }) => (row.original.is_masking_enabled ? 'Yes' : 'No'),
          },
        ] as ColumnDef<any>[])
      : []),
    {
      id: 'extensions',
      header: 'Extensions',
      cell: ({ row }) => getAgentExtensions(row.original),
    },
    {
      id: 'country_codes',
      header: 'Country Codes',
      cell: ({ row }) => {
        const rows = row.original.country_code_directions || [];
        const isLoggedIn = row.original.is_logged_in;
        if (rows.length === 0)
          return <span className="agents-list-empty-label">—</span>;
        return (
          <div className="flex items-center justify-center">
            <button
              type="button"
              disabled={isLoggedIn}
              onClick={() => {
                if (isLoggedIn) {
                  toast.error(
                    'Cannot change country codes: Agent is currently logged in. Please ask the agent to log out first.',
                  );
                  return;
                }
                setCcDirectionsData(rows);
                setCcDirectionsOwner(row.original.agent_name);
                setShowCcDirectionsModal(true);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 disabled:opacity-40 disabled:cursor-not-allowed"
              title={
                isLoggedIn
                  ? 'Cannot change country codes while agent is logged in'
                  : 'View country codes'
              }
              aria-label="View country codes"
            >
              <Flag className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      },
    },
    ...(!consolidatedCallCentre
      ? ([
          {
            id: 'allocation',
            header: 'Allocation Status',
            cell: ({ row }) => allocationStatusBodyTemplate(row.original),
          },
        ] as ColumnDef<any>[])
      : []),
    {
      id: 'allocated_clis',
      header: 'Allocated CLIs',
      cell: ({ row }) => (
        <AgentsListCenterCell>
          <button
            type="button"
            onClick={() => openViewClisModal(row.original)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
            title="View allocated CLIs"
            aria-label="View allocated CLIs"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </AgentsListCenterCell>
      ),
    },
    {
      id: 'status_toggle',
      header: 'Device / Login',
      cell: ({ row }) => {
        const rowData = row.original;
        const activeProfile = rowData.agentTelephony?.profile?.find(
          (p: any) => p.active_profile === true,
        );
        const localActiveType = activeProfile?.type;
        const localTypes: string[] =
          rowData?.agentTelephony?.profile?.map((item: any) => item.type) || [];
        const typeIconFor = (t: string) => {
          const up = String(t || '').toUpperCase();
          if (up.includes('MOBILE')) return <Smartphone className="h-4 w-4" />;
          if (up.includes('WEBRTC')) return <Radio className="h-4 w-4" />;
          if (up.includes('SOFT')) return <HeadphonesIcon className="h-4 w-4" />;
          return <Phone className="h-4 w-4" />;
        };
        return (
          <div className="flex items-center justify-center gap-1.5">
            {localTypes.map((t) => {
              const isActive = localActiveType === t;
              return (
                <span
                  key={t}
                  aria-label={isActive ? `Currently active: ${t}` : `${t} configured`}
                  title={isActive ? `Currently active: ${t}` : `${t} configured`}
                  className={
                    'inline-flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-inset ' +
                    (isActive
                      ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                      : 'bg-muted/60 text-muted-foreground ring-border/60')
                  }
                >
                  {typeIconFor(t)}
                </span>
              );
            })}
          </div>
        );
      },
    },
    !showActionsColumn
      ? null
      : consolidatedCallCentre
      ? ({
          id: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({ row }) => renderConsolidatedActions(row.original),
        } as ColumnDef<any>)
      : ({
          id: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({ row }) => {
            const rowData = row.original;
            return (
              <div className="flex items-center gap-1">
                {renderSwitchToAgentCell(rowData)}
                {isDirectParentOfAgent(rowData) && canEdit && (
                    <button
                      type="button"
                      onClick={() => handleEditAgent(rowData.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                      title="Edit"
                      aria-label="Edit"
                    >
                      <FiEdit className="h-3.5 w-3.5" />
                    </button>
                  )}
                {canBlock && rowData.linkedUser?.id && !loggedInCompanyDemo && (
                  <button
                    type="button"
                    onClick={() => handleBlockAgent(rowData.linkedUser.id)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                    title="Block agent (revokes login)"
                    aria-label="Block agent"
                  >
                    <Ban className="h-3.5 w-3.5" />
                  </button>
                )}
                {isDirectParentOfAgent(rowData) && canEdit && (
                    <button
                      type="button"
                      onClick={() => openAllocateCliModal(rowData)}
                      disabled={rowData.is_logged_in}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={
                        rowData.is_logged_in
                          ? 'Cannot allocate CLI while agent is logged in'
                          : 'Allocate CLI'
                      }
                      aria-label="Allocate CLI"
                    >
                      <PhoneForwarded className="h-3.5 w-3.5" />
                    </button>
                  )}
                <button
                  type="button"
                  onClick={() => setActivityAgent(rowData)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                  title="View agent activity"
                  aria-label="View activity"
                >
                  <ActivityIcon className="h-3.5 w-3.5" />
                </button>
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={() => setSipTraceAgent(rowData)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                    title="WebRTC trace (sign-in, calls reaching the agent, audio)"
                    aria-label="WebRTC trace"
                  >
                    <Radio className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setCallQualityAgent(rowData)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                  title="WebRTC call quality (MOS, loss, jitter, echo)"
                  aria-label="Call quality stats"
                >
                  <Signal className="h-3.5 w-3.5" />
                </button>
                {hierarchyTargetForAgent(rowData) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openHierarchyModal(rowData);
                    }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                    title="View hierarchy"
                    aria-label="View hierarchy"
                  >
                    <Network className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          },
        } as ColumnDef<any>),
  ].filter(Boolean) as ColumnDef<any>[];

  return (
    <FillPage>
      <PageHeader
        title="Agents"
        description="Provisioned agents — extensions, groups, availability, and quality snapshots."
        actions={
          <>
            {canAdd && canBulkUploadAgents() && <Button onClick={handleBulkAdd} variant="outline">Bulk Add</Button>}
            {canAdd && (
              <Button
                onClick={() => {
                  setAgentsReturnPath();
                  navigate('/create-agent');
                }}
              >
                <Plus className="h-4 w-4" />
                Create Agent
              </Button>
            )}
          </>
        }
      />
      {isSwitchingAccount && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center backdrop-blur-sm"
          style={{ pointerEvents: 'all' }}
        >
          <div className="flex min-w-[280px] items-center gap-3.5 rounded-2xl border border-border/60 bg-card px-7 py-5 shadow-[10px_10px_24px_rgba(163,177,198,0.5),-10px_-10px_24px_rgba(255,255,255,0.9)]">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <span className="text-sm font-semibold text-foreground">
              Switching account…
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkLogoutModal(true)}
                disabled={bulkLogoutLoading || selectedAgents.length === 0}
                className="gap-1.5"
              >
                Bulk Logout ({selectedAgents.length})
                {bulkLogoutLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              </Button>
            )}
            {agentList.length !== 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={buttonDisable}
                className="gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                Export
                {buttonDisable && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              </Button>
            )}
            <div className="relative ml-auto">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search agents…"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="input-embossed h-10 w-[260px] rounded-md pl-8 pr-3 text-sm"
              />
            </div>
          </div>
          <div className="scroll-container agents-list-scroll">
            <DataTable
              tableId="agents"
              columns={columns}
              data={filteredAgents}
              pageSize={10}
              emptyTitle="No agents found."
              disableExport
              getRowId={(row) => String(row.id)}
              initialColumnPinning={{ right: ['actions'] }}
            />
          </div>
        </>
      )}

      <AgentActivityModal
        show={!!activityAgent}
        onHide={() => setActivityAgent(null)}
        agentId={activityAgent?.id}
        agentName={activityAgent?.agent_name}
        agentNumber={activityAgent?.agent_number}
      />

      <SipTraceModal
        show={isSuperAdmin && !!sipTraceAgent}
        onHide={() => setSipTraceAgent(null)}
        agentId={sipTraceAgent?.id}
        agentName={sipTraceAgent?.agent_name}
        agentNumber={sipTraceAgent?.agent_number}
      />

      <RenderUserHierarchyModal
        show={showHierarchyModal}
        setShow={setShowHierarchyModal}
        user={hierarchyUser}
      />

      <AgentCallQualityModal
        show={!!callQualityAgent}
        onHide={() => setCallQualityAgent(null)}
        agentId={callQualityAgent?.id}
        agentName={callQualityAgent?.agent_name}
        agentNumber={callQualityAgent?.agent_number}
      />

      {/* View CLIs */}
      <Dialog open={showViewClisModal} onOpenChange={setShowViewClisModal}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Phone className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">Allocated CLIs</div>
                <div className="text-xs font-normal text-muted-foreground">
                  {viewClisAgent?.agent_name
                    ? `Outbound caller IDs allocated to ${viewClisAgent.agent_name}.`
                    : 'Outbound caller IDs allocated to this agent.'}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-xl border border-border/60 bg-muted/40 px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Agent
                </span>
                <div className="text-sm font-semibold text-foreground">
                  {viewClisAgent?.agent_name || '—'}
                </div>
              </div>
              <div className="ml-auto">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Total CLIs
                </span>
                <div>
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary ring-1 ring-inset ring-primary/25 tabular-nums">
                    {viewClisList.length}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {viewClisLoading ? (
            <div className="flex items-center justify-center rounded-xl border border-border/60 bg-card py-8 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : viewClisList.length === 0 ? (
            <div className="rounded-xl border border-border/60 bg-card py-8 text-center text-sm text-muted-foreground">
              No CLIs allocated
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="w-12 px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      #
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Number
                    </th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {viewClisList.map((n, idx) => (
                    <tr
                      key={n.id}
                      className={
                        'border-t border-border/40 transition-colors hover:bg-muted/30 ' +
                        (idx % 2 === 1 ? 'bg-muted/15' : '')
                      }
                    >
                      <td className="px-4 py-2.5 text-xs tabular-nums text-muted-foreground">
                        {idx + 1}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-sm font-medium text-foreground">
                        {n.actual_number}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {n.is_default ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
                            Default
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
                            Allocated
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewClisModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agent Details */}
      <Dialog
        open={showAgentDetailsModal}
        onOpenChange={(next) => {
          if (!next) {
            setShowAgentDetailsModal(false);
            setSelectedAgentDetails(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <User className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">Agent Details</div>
                <div className="text-xs font-normal text-muted-foreground">
                  {selectedAgentDetails?.agent_name || 'Read-only agent profile summary.'}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedAgentDetails &&
            (() => {
              const isBlocked =
                selectedAgentDetails.account_status === 0 ||
                selectedAgentDetails.account_status === 'Blocked';
              const rows: {
                icon: any;
                label: string;
                value: any;
                tone?: 'default' | 'success' | 'danger' | 'primary';
              }[] = [
                {
                  icon: <Phone className="h-3.5 w-3.5" />,
                  label: 'Agent Number',
                  value: selectedAgentDetails.agent_number || '—',
                },
                {
                  icon: <Mail className="h-3.5 w-3.5" />,
                  label: 'Email ID',
                  value: selectedAgentDetails.email_id || '—',
                },
                {
                  icon: <User className="h-3.5 w-3.5" />,
                  label: 'Username',
                  value:
                    selectedAgentDetails.username ||
                    selectedAgentDetails.linkedUser?.username ||
                    '—',
                },
                {
                  icon: <User className="h-3.5 w-3.5" />,
                  label: 'Created By',
                  value: selectedAgentDetails.created_by || '—',
                },
                {
                  icon: <Calendar className="h-3.5 w-3.5" />,
                  label: 'Joining Date',
                  value: selectedAgentDetails.created_at || '—',
                },
                {
                  icon: <Shield className="h-3.5 w-3.5" />,
                  label: 'Assigned ACL',
                  value: formatAgentAclDisplay(selectedAgentDetails),
                  tone: 'primary',
                },
              ];
              if (consolidatedCallCentre) {
                rows.push(
                  {
                    icon: <CalendarClock className="h-3.5 w-3.5" />,
                    label: 'Expiry Date',
                    value: formatDateTime(selectedAgentDetails.expiry_date) || '—',
                  },
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Account Status',
                    value: isBlocked ? 'Blocked' : 'Active',
                    tone: isBlocked ? 'danger' : 'success',
                  },
                  {
                    icon: <User className="h-3.5 w-3.5" />,
                    label: 'Access',
                    value:
                      selectedAgentDetails.access === 'team_lead'
                        ? 'Team Lead'
                        : selectedAgentDetails.access === 'regular'
                        ? 'Regular'
                        : selectedAgentDetails.access || '—',
                  },
                  {
                    icon: <User className="h-3.5 w-3.5" />,
                    label: 'First Call Acceptance',
                    value: selectedAgentDetails.first_call_acceptance || '—',
                  },
                  {
                    icon: <Phone className="h-3.5 w-3.5" />,
                    label: 'CLI Option (Webrtc)',
                    value: selectedAgentDetails.cli_option || '—',
                  },
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Outbound Enabled',
                    value: selectedAgentDetails.is_outbound_enabled ? 'Yes' : 'No',
                    tone: selectedAgentDetails.is_outbound_enabled ? 'success' : 'default',
                  },
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Number Masking',
                    value: selectedAgentDetails.is_masking_enabled ? 'Yes' : 'No',
                    tone: selectedAgentDetails.is_masking_enabled ? 'success' : 'default',
                  },
                  {
                    icon: <User className="h-3.5 w-3.5" />,
                    label: 'Allocation Status',
                    value:
                      Number(selectedAgentDetails.is_allocated) === 0 ? 'Available' : 'Allocated',
                    tone: Number(selectedAgentDetails.is_allocated) === 0 ? 'success' : 'primary',
                  },
                );
              }
              const toneClass = (t?: string) => {
                if (t === 'success')
                  return 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300';
                if (t === 'danger')
                  return 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300';
                if (t === 'primary')
                  return 'bg-primary/10 text-primary ring-primary/25';
                return '';
              };
              return (
                <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
                  <div className="grid grid-cols-1 divide-y divide-border/40 md:grid-cols-2 md:divide-y-0">
                    {rows.map((r, idx) => {
                      const rightBorder =
                        idx % 2 === 0 ? 'md:border-r md:border-border/40' : '';
                      const topBorder = idx >= 2 ? 'md:border-t md:border-border/40' : '';
                      return (
                        <div
                          key={r.label}
                          className={
                            'flex items-center gap-3 px-4 py-3 ' +
                            rightBorder +
                            ' ' +
                            topBorder
                          }
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
                            {r.icon}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              {r.label}
                            </div>
                            <div className="mt-0.5">
                              {r.tone ? (
                                <span
                                  className={
                                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ' +
                                    toneClass(r.tone)
                                  }
                                >
                                  {r.value}
                                </span>
                              ) : (
                                <span className="break-words text-sm font-semibold text-foreground">
                                  {r.value}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAgentDetailsModal(false);
                setSelectedAgentDetails(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate CLI */}
      <Dialog
        open={showAllocateCliModal}
        onOpenChange={(next) => {
          if (!next && !savingCliDid) setShowAllocateCliModal(false);
        }}
      >
        <DialogContent
          className="max-h-[85vh] max-w-lg overflow-visible allocate-cli-modal"
          onPointerDownOutside={(e) => {
            if (
              isAllocateCliSelectNode(e.target) ||
              isAllocateCliSelectNode(e.detail?.originalEvent?.target)
            ) {
              e.preventDefault();
            }
          }}
          onFocusOutside={(e) => {
            if (
              isAllocateCliSelectNode(e.target) ||
              isAllocateCliSelectNode(e.detail?.originalEvent?.target)
            ) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            if (
              isAllocateCliSelectNode(e.target) ||
              isAllocateCliSelectNode(e.detail?.originalEvent?.target)
            ) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <PhoneForwarded className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">Allocate CLI</div>
                <div className="text-xs font-normal text-muted-foreground">
                  Choose which caller IDs this agent may use for outbound calls.
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-xl border border-border/60 bg-muted/40 px-4 py-3">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Agent
            </span>
            <div className="text-sm font-semibold text-foreground">
              {allocatingAgent?.agent_name || '—'}
            </div>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Select CLI</label>
              <div className="relative z-20">
                <ReactSelect
                  isMulti
                  classNamePrefix="allocate-cli-select"
                  value={availableCliDids
                    .filter((n) => selectedCliDidIds.includes(Number(n.id)))
                    .map((n) => ({ value: Number(n.id), label: `${String(n.actual_number)} (CLI)` }))}
                  options={availableCliDids.map((n) => ({
                    value: Number(n.id),
                    label: `${String(n.actual_number)} (CLI)`,
                  }))}
                  onChange={(sel: any) => handleCliDidSelect((sel || []).map((o) => o.value))}
                  isDisabled={loadingCliDids || savingCliDid}
                  placeholder="Select CLI(s)"
                  className="w-100"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 50, pointerEvents: 'auto' }),
                    menuList: (base) => ({ ...base, maxHeight: 240 }),
                  }}
                />
              </div>
              <div className="text-xs text-muted-foreground">
                Default CLI:{' '}
                <strong>
                  {(() => {
                    const defaultId = selectedCliDidIds?.[0];
                    const defaultDid = availableCliDids.find((n) => Number(n.id) === Number(defaultId));
                    return defaultDid ? `${defaultDid.actual_number}` : 'Not selected';
                  })()}
                </strong>
              </div>
            </div>

            {!!selectedCliDidIds?.length && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Dedicated CLI (one number)
                </label>
                <div className="max-h-[200px] overflow-y-auto rounded-lg border border-border/60 bg-card p-2">
                  <label className="mb-1 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-muted/40">
                    <input
                      type="radio"
                      id="dedicated-cli-none"
                      name="agentDedicatedCli"
                      checked={dedicatedCliNumberId === null}
                      onChange={() => setDedicatedCliNumberId(null)}
                      disabled={savingCliDid}
                      className="accent-primary"
                    />
                    <span className="text-muted-foreground">None</span>
                  </label>
                  {selectedCliDidIds.map((didId) => {
                    const did = availableCliDids.find(
                      (n) => Number(n.id) === Number(didId),
                    );
                    if (!did) return null;
                    return (
                      <label
                        key={`dedicated-cli-${didId}`}
                        className="mb-0.5 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-muted/40"
                      >
                        <input
                          type="radio"
                          id={`dedicated-cli-${didId}`}
                          name="agentDedicatedCli"
                          checked={dedicatedCliNumberId === Number(didId)}
                          onChange={() => setDedicatedCliNumberId(Number(didId))}
                          disabled={savingCliDid}
                          className="accent-primary"
                        />
                        <span className="font-mono">{did.actual_number}</span>
                        <span className="text-xs text-muted-foreground">(CLI)</span>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-primary/25 bg-primary/[0.06] p-3 text-xs text-foreground">
                  <FiInfo size={18} className="mt-0 flex-shrink-0 text-primary" aria-hidden />
                  <span>
                    For a dedicated CLI, open <strong>Numbers</strong> → inbound routing for
                    that number and set the <strong>time schedule</strong> as needed.
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAllocateCliModal(false)}
              disabled={savingCliDid}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveAllocatedCliDid} disabled={savingCliDid} className="gap-2">
              {savingCliDid && <Loader2 className="h-4 w-4 animate-spin" />}
              {savingCliDid ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Add */}
      <Dialog open={showBulkAddModal} onOpenChange={setShowBulkAddModal}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <User className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">Bulk Add Agents</div>
                <div className="text-xs font-normal text-muted-foreground">
                  Upload a CSV file to onboard multiple agents at once.
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <label htmlFor="formFile" className="text-sm font-medium text-foreground">
                Upload .csv File
              </label>
              <Input
                id="formFile"
                type="file"
                accept=".csv"
                onChange={handleBulkAddFileChange}
              />
            </div>
            <InfoNote>
              The details uploaded for each agent are also used to automatically create a corresponding user.
              Agent Name becomes the user&apos;s display name. Username and Password are used exactly as
              provided for login.
              <br />
              <br />
              <strong>Required Fields:</strong> Agent Name, Agent Number, Email ID, Username, Password,
              Access, Type (must be allowed by your Agents ACL: Mobile / Soft Phone / WEBRTC), Country Code (if Type includes Mobile), Cli Option (if Type includes WEBRTC),
              Extension ID (if Type includes Soft Phone or WEBRTC)
              <br />
              <strong>Optional Fields:</strong> Description, Is Outbound Enabled, Masking, First Call
              Connect, Country Code Direction IDs, ACL Settings ID, Account Type, Expiry Date, Domain, Panel
              Display Name, Logo Path
              <br />
              <em>Username is separate from Agent Name. Download the template for the exact format.</em>
            </InfoNote>
            <Button
              size="sm"
              onClick={(event) => {
                const { columns: cols, requirements, filename, sample_data } = getColumnsAndFilename();
                downloadTemplate(event, cols, filename, requirements, sample_data);
              }}
            >
              Download Template
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAddModal(false)}>
              Close
            </Button>
            <Button onClick={handleBulkAddSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Result */}
      <Dialog
        open={showBulkResultModal}
        onOpenChange={(next) => {
          if (!next) {
            setShowBulkResultModal(false);
            setBulkUploadResult(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Info className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">
                  Bulk Upload Result
                </div>
                <div className="text-xs font-normal text-muted-foreground">
                  Summary of the agents processed from the uploaded CSV.
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          {bulkUploadResult && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-border/60 bg-card px-4 py-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Processed
                  </div>
                  <div className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                    {bulkUploadResult.totalProcessed}
                  </div>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-900/40 dark:bg-emerald-950/30">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                    Created
                  </div>
                  <div className="mt-1 text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                    {bulkUploadResult.successCount}
                  </div>
                </div>
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 dark:border-rose-900/40 dark:bg-rose-950/30">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-rose-700 dark:text-rose-400">
                    Failed
                  </div>
                  <div className="mt-1 text-2xl font-bold tabular-nums text-rose-700 dark:text-rose-300">
                    {bulkUploadResult.failedCount}
                  </div>
                </div>
              </div>

              {bulkUploadResult.successRows?.length > 0 && (
                <div className="space-y-2">
                  <h5 className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Agents Created ({bulkUploadResult.successRows.length})
                  </h5>
                  <textarea
                    readOnly
                    rows={4}
                    className="w-full rounded-md border border-border/60 bg-card p-3 font-mono text-xs text-foreground shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)] outline-none focus:border-ring"
                    value={bulkUploadResult.successRows
                      .map((item, index) => formatSuccessAgent(item, index))
                      .join('\n')}
                  />
                </div>
              )}

              {bulkUploadResult.failedRows?.length > 0 && (
                <div className="space-y-2">
                  <h5 className="flex items-center gap-2 text-sm font-semibold text-rose-700 dark:text-rose-400">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-rose-500" />
                    Failed Rows ({bulkUploadResult.failedRows.length})
                  </h5>
                  <textarea
                    readOnly
                    rows={6}
                    className="w-full rounded-md border border-border/60 bg-card p-3 font-mono text-xs text-foreground shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)] outline-none focus:border-ring"
                    value={bulkUploadResult.failedRows
                      .map((item, index) => formatFailedAgent(item, index))
                      .join('\n')}
                  />
                </div>
              )}

              {bulkUploadResult.successCount === 0 &&
                bulkUploadResult.failedCount === 0 && (
                  <div className="rounded-xl border border-border/60 bg-muted/40 p-6 text-center text-sm text-muted-foreground">
                    No rows were processed from the uploaded file.
                  </div>
                )}
            </>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowBulkResultModal(false);
                setBulkUploadResult(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Logout confirm */}
      <ConfirmDialog
        open={showBulkLogoutModal}
        onOpenChange={(open) => {
          if (!open && bulkLogoutLoading) return;
          setShowBulkLogoutModal(open);
        }}
        title="Bulk Logout Agents"
        description="Are you sure you want to logged out the Agent(s)."
        confirmLabel="Save"
        cancelLabel="Close"
        loading={bulkLogoutLoading}
        onConfirm={handleAgentLogout}
      />

      <ConfirmDialog
        open={pendingTypeLogin !== null}
        onOpenChange={(o) => {
          if (!o && !isLoggingIn) setPendingTypeLogin(null);
        }}
        title={
          pendingTypeLogin?.isActive
            ? `Re-login as ${pendingTypeLogin?.type}?`
            : `Log in as ${pendingTypeLogin?.type}?`
        }
        description={
          <>
            Are you sure you want to log in{' '}
            <span className="font-medium text-foreground">
              {pendingTypeLogin?.agentName}
            </span>{' '}
            as{' '}
            <span className="font-medium text-foreground">
              {pendingTypeLogin?.type}
            </span>
            ?
          </>
        }
        confirmLabel="Log in"
        loading={isLoggingIn}
        onConfirm={confirmTypeLogin}
      />

      <CountryCodeDirectionsModal
        show={showCcDirectionsModal}
        onHide={() => {
          setShowCcDirectionsModal(false);
          setCcDirectionsData([]);
          setCcDirectionsOwner('');
        }}
        title="Agent Country Codes"
        ownerLabel="Agent Name"
        ownerName={ccDirectionsOwner}
        directions={ccDirectionsData}
      />

      <RenderInactiveUserModal
        show={enableInactiveModal}
        setShow={setEnableInactiveModal}
        setConfirmUserInactive={setConfirmUserInactive}
        clearSelectedData={() => {}}
      />

      {consolidatedCallCentre && (
        <>
          <RenderShowPasswordModal
            show={enableShowPasswordModal}
            setShow={setEnableShowPasswordModal}
            setConfirmShowPassword={setConfirmShowPassword}
            passwordValue={showPasswordUser}
            userId={showPasswordId}
            onPasswordReset={() => {
              if (showPasswordId && activeUserAccessRole) {
                getApiCall(`${API_USERS_LIST}/${showPasswordId}`, { role: activeUserAccessRole })
                  .then((resp: any) => {
                    if (resp?.actual_password) setShowPasswordUser(`Password is : ${resp.actual_password}`);
                  })
                  .catch(() => {});
              }
            }}
          />
        </>
      )}
    </FillPage>
  );
};

export default Agents;
